import React from "react";
import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Alert from "@cloudscape-design/components/alert";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Grid from "@cloudscape-design/components/grid";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Select from "@cloudscape-design/components/select";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import CollectionPreferences from "@cloudscape-design/components/collection-preferences";
import Modal from "@cloudscape-design/components/modal";
import Tabs from "@cloudscape-design/components/tabs";
import Toggle from "@cloudscape-design/components/toggle";
import Container from "@cloudscape-design/components/container";
import ColumnLayout from "@cloudscape-design/components/column-layout";

const TICKETS_TAB_SESSION_KEY = "prizeTickets.activeTab";
const TICKETS_TAB_IDS = ["tickets", "gift"];

class PrizeTickets extends React.Component {
	constructor(props) {
		super(props);

		this.state = this.getInitialState();
	}

	componentDidMount() {
		this.loadTickets();

		if (this.state.activeTicketsTab === "gift") {
			this.loadGiftStats({ page: 1 });
		}
	}

	getStoredTicketsTab() {
		try {
			const activeTab = sessionStorage.getItem(TICKETS_TAB_SESSION_KEY);

			if (TICKETS_TAB_IDS.includes(activeTab)) return activeTab;
		} catch (error) {
			return null;
		}

		return null;
	}

	saveStoredTicketsTab(activeTab) {
		try {
			sessionStorage.setItem(TICKETS_TAB_SESSION_KEY, activeTab);
		} catch (error) {
			// Ignore storage failures; the selected tab is still kept in component state.
		}
	}

	getDefaultRtpCompensationConfig() {
		return {
			enabled: false,
			windowRounds: 10,
			rules: [
				{ min: "85", minOperator: ">=", max: "", maxOperator: null, tickets: "0" },
				{ min: "60", minOperator: ">", max: "85", maxOperator: "<", tickets: "1" },
				{ min: "", minOperator: null, max: "60", maxOperator: "<", tickets: "2" },
			],
		};
	}

	getEmptyDateRange() {
		return {
			type: "absolute",
			startDate: "",
			endDate: "",
		};
	}

	getDateRangeValue(value) {
		if (!value || (!value.startDate && !value.endDate)) return this.getEmptyDateRange();

		return {
			type: "absolute",
			startDate: value.startDate || "",
			endDate: value.endDate || "",
		};
	}

	getInitialState() {
		const pageSize = Number(localStorage.getItem("tickets.perPage")) || 20;
		const giftPageSize = Number(localStorage.getItem("giftTickets.perPage")) || 20;

		return {
			activeTicketsTab: this.getStoredTicketsTab() || "tickets",
			loaded: false,
			data: [],
			selectedItems: [],
			showSettingsModal: false,
			settingsModalType: "tickets",
			ticketPrice: "",
			minTicketsToBuy: "",
			rtpCompensation: this.getDefaultRtpCompensationConfig(),
			settingsError: "",
			isSettingsLoading: false,
			writeOffItems: [],
			showWriteOffAllModal: false,
			showWriteOffAmountModal: false,
			writeOffAmount: "",
			writeOffError: "",
			isWriteOffLoading: false,
			dateRange: this.getEmptyDateRange(),
			ticketType: { value: "all", label: "All" },
			uid: "",
			periodTicketsFrom: "",
			periodTicketsTo: "",
			currentTicketsFrom: "",
			currentTicketsTo: "",
			sorting: {
				sortingColumn: {
					column: "lastTicketDate",
					order: "DESC",
				},
				isDescending: true,
			},
			pagination: {
				current: 1,
				total: 1,
				count: 0,
			},
			pageSize,
			gift: {
				loaded: false,
				events: [],
				daily: [],
				summary: {
					totalTickets: 0,
					playersCount: 0,
				},
				dateRange: this.getEmptyDateRange(),
				uid: "",
				ticketsFrom: "",
				ticketsTo: "",
				rtpFrom: "",
				rtpTo: "",
				sorting: {
					sortingColumn: {
						column: "issuedAt",
						order: "DESC",
					},
					isDescending: true,
				},
				pagination: {
					current: 1,
					total: 1,
					count: 0,
				},
				pageSize: giftPageSize,
				error: {
					text: "",
					code: 0,
					timestamp: 0,
				},
			},
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		};
	}

	getTypeOptions() {
		return [
			{ value: "all", label: "All" },
			{ value: "buy", label: "Purchased" },
			{ value: "win", label: "Won" },
		];
	}

	getRtpOperatorOptions(type) {
		return type === "min"
			? [
					{ value: ">", label: ">" },
					{ value: ">=", label: ">=" },
			  ]
			: [
					{ value: "<", label: "<" },
					{ value: "<=", label: "<=" },
			  ];
	}

	getSelectedOperator(value, type) {
		if (!value) return null;

		return this.getRtpOperatorOptions(type).find((option) => option.value === value) || null;
	}

	getNullableInputValue(value) {
		if (value === null || typeof value === "undefined") return "";

		return String(value);
	}

	normalizeRtpCompensationForState(config) {
		const source = config && typeof config === "object" ? config : this.getDefaultRtpCompensationConfig();
		const defaultConfig = this.getDefaultRtpCompensationConfig();
		const rules = Array.isArray(source.rules) && source.rules.length ? source.rules : defaultConfig.rules;

		return {
			enabled: Boolean(source.enabled),
			windowRounds: this.getNullableInputValue(source.windowRounds || defaultConfig.windowRounds),
			rules: rules.map((rule) => ({
				min: this.getNullableInputValue(rule.min),
				minOperator: rule.minOperator || null,
				max: this.getNullableInputValue(rule.max),
				maxOperator: rule.maxOperator || null,
				tickets: this.getNullableInputValue(rule.tickets),
			})),
		};
	}

	getRtpValueForPayload(value, fieldName) {
		if (value === "" || value === null || typeof value === "undefined") return null;

		const normalized = Number(value);
		if (!Number.isFinite(normalized) || normalized < 0 || normalized > 100) {
			throw new Error(`${fieldName} should be a number from 0 to 100`);
		}

		return normalized;
	}

	getRtpRuleInterval(rule) {
		const minOperator = rule.minOperator || (rule.min !== null ? ">=" : null);
		const maxOperator = rule.maxOperator || (rule.max !== null ? "<" : null);

		return {
			lower: rule.min === null ? 0 : rule.min,
			lowerInclusive: rule.min === null ? true : minOperator === ">=",
			upper: rule.max === null ? 100 : rule.max,
			upperInclusive: rule.max === null ? true : maxOperator === "<=",
		};
	}

	validateRtpRuleOverlap(rules) {
		const intervals = rules.map((rule) => this.getRtpRuleInterval(rule));

		for (let i = 0; i < intervals.length; i++) {
			for (let j = i + 1; j < intervals.length; j++) {
				const a = intervals[i];
				const b = intervals[j];
				const lower = Math.max(a.lower, b.lower);
				const upper = Math.min(a.upper, b.upper);

				if (lower < upper) {
					throw new Error("RTP rules cannot overlap");
				}

				if (lower === upper) {
					const aContains = (lower > a.lower || (lower === a.lower && a.lowerInclusive)) && (lower < a.upper || (lower === a.upper && a.upperInclusive));
					const bContains = (lower > b.lower || (lower === b.lower && b.lowerInclusive)) && (lower < b.upper || (lower === b.upper && b.upperInclusive));

					if (aContains && bContains) {
						throw new Error("RTP rules cannot overlap");
					}
				}
			}
		}
	}

	getRtpCompensationPayload() {
		const { rtpCompensation } = this.state;
		const windowRounds = parseInt(rtpCompensation.windowRounds, 10);

		if (!Number.isInteger(windowRounds) || windowRounds < 1) {
			throw new Error("Rounds for RTP calculation should be a positive integer");
		}

		if (!Array.isArray(rtpCompensation.rules) || !rtpCompensation.rules.length) {
			throw new Error("At least one RTP rule is required");
		}

		const rules = rtpCompensation.rules.map((rule, index) => {
			const min = this.getRtpValueForPayload(rule.min, `Rule ${index + 1} minimum RTP`);
			const max = this.getRtpValueForPayload(rule.max, `Rule ${index + 1} maximum RTP`);

			if (min === null && max === null) {
				throw new Error(`Rule ${index + 1} should have at least one RTP bound`);
			}

			if (min !== null && !rule.minOperator) {
				throw new Error(`Rule ${index + 1} minimum operator is required`);
			}

			if (max !== null && !rule.maxOperator) {
				throw new Error(`Rule ${index + 1} maximum operator is required`);
			}

			if (min !== null && max !== null && min > max) {
				throw new Error(`Rule ${index + 1} minimum RTP should be less than or equal to maximum RTP`);
			}

			if (min !== null && max !== null && min === max && (rule.minOperator === ">" || rule.maxOperator === "<")) {
				throw new Error(`Rule ${index + 1} has an empty RTP interval`);
			}

			const tickets = parseInt(rule.tickets, 10);
			if (!Number.isInteger(tickets) || tickets < 0) {
				throw new Error(`Rule ${index + 1} tickets should be a non-negative integer`);
			}

			return {
				min,
				minOperator: min === null ? null : rule.minOperator,
				max,
				maxOperator: max === null ? null : rule.maxOperator,
				tickets,
			};
		});

		this.validateRtpRuleOverlap(rules);

		return {
			enabled: Boolean(rtpCompensation.enabled),
			windowRounds,
			rules,
		};
	}

	updateRtpCompensation = (patch) => {
		this.setState((prevState) => ({
			rtpCompensation: {
				...prevState.rtpCompensation,
				...patch,
			},
			settingsError: "",
		}));
	};

	updateRtpRule = (index, patch) => {
		this.setState((prevState) => ({
			rtpCompensation: {
				...prevState.rtpCompensation,
				rules: prevState.rtpCompensation.rules.map((rule, ruleIndex) => (ruleIndex === index ? { ...rule, ...patch } : rule)),
			},
			settingsError: "",
		}));
	};

	addRtpRule = () => {
		this.setState((prevState) => ({
			rtpCompensation: {
				...prevState.rtpCompensation,
				rules: [...prevState.rtpCompensation.rules, { min: "", minOperator: null, max: "", maxOperator: null, tickets: "1" }],
			},
			settingsError: "",
		}));
	};

	removeRtpRule = (index) => {
		this.setState((prevState) => ({
			rtpCompensation: {
				...prevState.rtpCompensation,
				rules: prevState.rtpCompensation.rules.filter((_, ruleIndex) => ruleIndex !== index),
			},
			settingsError: "",
		}));
	};

	isDateRangeValid(range) {
		if (!range || (!range.startDate && !range.endDate)) return { valid: true };

		if (range.type !== "absolute") return { valid: false, errorMessage: "Only absolute date range is supported" };

		if (!range.startDate || !range.endDate) return { valid: true };

		if (range.startDate > range.endDate) {
			return { valid: false, errorMessage: "Start date should be earlier than end date" };
		}

		return { valid: true };
	}

	getUidValue() {
		const uidList = this.state.uid
			.split(/[\s,]+/)
			.map((item) => item.trim())
			.filter((item) => item.length)
			.map((item) => Number(item))
			.filter((item) => Number.isFinite(item));

		if (!uidList.length) return undefined;
		if (uidList.length === 1) return uidList[0];

		return uidList;
	}

	getGiftUidValue() {
		const uidList = this.state.gift.uid
			.split(/[\s,]+/)
			.map((item) => item.trim())
			.filter((item) => item.length)
			.map((item) => Number(item))
			.filter((item) => Number.isFinite(item));

		if (!uidList.length) return undefined;
		if (uidList.length === 1) return uidList[0];

		return uidList;
	}

	getTicketsFiltersValidation() {
		if (this.state.periodTicketsFrom !== "" && (!Number.isFinite(Number(this.state.periodTicketsFrom)) || Number(this.state.periodTicketsFrom) < 0)) {
			return { valid: false, errorMessage: "Tickets in period from should be a non-negative number" };
		}

		if (this.state.periodTicketsTo !== "" && (!Number.isFinite(Number(this.state.periodTicketsTo)) || Number(this.state.periodTicketsTo) < 0)) {
			return { valid: false, errorMessage: "Tickets in period to should be a non-negative number" };
		}

		if (this.state.periodTicketsFrom !== "" && this.state.periodTicketsTo !== "" && Number(this.state.periodTicketsFrom) > Number(this.state.periodTicketsTo)) {
			return { valid: false, errorMessage: "Tickets in period from should be less than or equal to tickets in period to" };
		}

		if (this.state.currentTicketsFrom !== "" && (!Number.isFinite(Number(this.state.currentTicketsFrom)) || Number(this.state.currentTicketsFrom) < 0)) {
			return { valid: false, errorMessage: "Current tickets from should be a non-negative number" };
		}

		if (this.state.currentTicketsTo !== "" && (!Number.isFinite(Number(this.state.currentTicketsTo)) || Number(this.state.currentTicketsTo) < 0)) {
			return { valid: false, errorMessage: "Current tickets to should be a non-negative number" };
		}

		if (this.state.currentTicketsFrom !== "" && this.state.currentTicketsTo !== "" && Number(this.state.currentTicketsFrom) > Number(this.state.currentTicketsTo)) {
			return { valid: false, errorMessage: "Current tickets from should be less than or equal to current tickets to" };
		}

		return { valid: true };
	}

	getGiftFiltersValidation() {
		const gift = this.state.gift;

		if (gift.ticketsFrom !== "" && (!Number.isFinite(Number(gift.ticketsFrom)) || Number(gift.ticketsFrom) < 0)) {
			return { valid: false, errorMessage: "Tickets issued from should be a non-negative number" };
		}

		if (gift.ticketsTo !== "" && (!Number.isFinite(Number(gift.ticketsTo)) || Number(gift.ticketsTo) < 0)) {
			return { valid: false, errorMessage: "Tickets issued to should be a non-negative number" };
		}

		if (gift.ticketsFrom !== "" && gift.ticketsTo !== "" && Number(gift.ticketsFrom) > Number(gift.ticketsTo)) {
			return { valid: false, errorMessage: "Tickets issued from should be less than or equal to tickets issued to" };
		}

		if (gift.rtpFrom !== "" && (!Number.isFinite(Number(gift.rtpFrom)) || Number(gift.rtpFrom) < 0 || Number(gift.rtpFrom) > 100)) {
			return { valid: false, errorMessage: "RTP from should be a number from 0 to 100" };
		}

		if (gift.rtpTo !== "" && (!Number.isFinite(Number(gift.rtpTo)) || Number(gift.rtpTo) < 0 || Number(gift.rtpTo) > 100)) {
			return { valid: false, errorMessage: "RTP to should be a number from 0 to 100" };
		}

		if (gift.rtpFrom !== "" && gift.rtpTo !== "" && Number(gift.rtpFrom) > Number(gift.rtpTo)) {
			return { valid: false, errorMessage: "RTP from should be less than or equal to RTP to" };
		}

		return { valid: true };
	}

	getRequestData(page = this.state.pagination.current, pageSize = this.state.pageSize, sorting = this.state.sorting.sortingColumn) {
		const data = {
			type: this.state.ticketType.value,
			page,
			limit: pageSize,
			sorting: {
				column: sorting.column,
				order: sorting.order,
			},
		};

		const uid = this.getUidValue();
		if (this.state.dateRange.startDate && this.state.dateRange.endDate) {
			data.dateRange = {
				start: this.state.dateRange.startDate,
				end: this.state.dateRange.endDate,
			};
		}

		if (typeof uid !== "undefined") data.uid = uid;
		if (this.state.periodTicketsFrom !== "" || this.state.periodTicketsTo !== "") {
			data.periodTicketsRange = {};
			if (this.state.periodTicketsFrom !== "") data.periodTicketsRange.from = Number(this.state.periodTicketsFrom);
			if (this.state.periodTicketsTo !== "") data.periodTicketsRange.to = Number(this.state.periodTicketsTo);
		}
		if (this.state.currentTicketsFrom !== "" || this.state.currentTicketsTo !== "") {
			data.currentTicketsRange = {};
			if (this.state.currentTicketsFrom !== "") data.currentTicketsRange.from = Number(this.state.currentTicketsFrom);
			if (this.state.currentTicketsTo !== "") data.currentTicketsRange.to = Number(this.state.currentTicketsTo);
		}

		return data;
	}

	getGiftRequestData(page = this.state.gift.pagination.current, pageSize = this.state.gift.pageSize, sorting = this.state.gift.sorting.sortingColumn) {
		const gift = this.state.gift;
		const data = {
			page,
			limit: pageSize,
			sorting: {
				column: sorting.column,
				order: sorting.order,
			},
		};

		const uid = this.getGiftUidValue();
		if (gift.dateRange.startDate && gift.dateRange.endDate) {
			data.dateRange = {
				start: gift.dateRange.startDate,
				end: gift.dateRange.endDate,
			};
		}

		if (typeof uid !== "undefined") data.uid = uid;
		if (gift.ticketsFrom !== "" || gift.ticketsTo !== "") {
			data.ticketsRange = {};
			if (gift.ticketsFrom !== "") data.ticketsRange.from = Number(gift.ticketsFrom);
			if (gift.ticketsTo !== "") data.ticketsRange.to = Number(gift.ticketsTo);
		}
		if (gift.rtpFrom !== "" || gift.rtpTo !== "") {
			data.rtpRange = {};
			if (gift.rtpFrom !== "") data.rtpRange.from = Number(gift.rtpFrom);
			if (gift.rtpTo !== "") data.rtpRange.to = Number(gift.rtpTo);
		}

		return data;
	}

	getSelectedUids() {
		return [...new Set(this.state.selectedItems.map((item) => Number(item.uid)).filter((uid) => Number.isInteger(uid) && uid > 0))];
	}

	getWriteOffUids() {
		return [...new Set(this.state.writeOffItems.map((item) => Number(item.uid)).filter((uid) => Number.isInteger(uid) && uid > 0))];
	}

	openSettingsModal = async (settingsModalType = "tickets") => {
		this.setState({
			showSettingsModal: true,
			settingsModalType,
			isSettingsLoading: true,
		});

		try {
			const config = await this.props.Interface.getTicketsConfig();

			this.setState({
				ticketPrice: typeof config?.amount === "number" ? String(config.amount) : "",
				minTicketsToBuy: typeof config?.minCount === "number" ? String(config.minCount) : "",
				rtpCompensation: this.normalizeRtpCompensationForState(config?.rtpCompensation),
				settingsError: "",

				isSettingsLoading: false,
			});
		} catch (error) {
			this.props.Interface.addNoty("Error", error?.text || error?.message || "Failed to load ticket settings");
			this.setState({
				isSettingsLoading: false,
			});
		}
	};

	closeSettingsModal = () => {
		if (this.state.isSettingsLoading) return;

		this.setState({
			showSettingsModal: false,
		});
	};

	handleSettingsSave = async () => {
		if (this.state.settingsModalType === "tickets") {
			return this.handleTicketSettingsSave();
		}

		return this.handleRtpGiftSettingsSave();
	};

	handleTicketSettingsSave = async () => {
		const { ticketPrice, minTicketsToBuy } = this.state;

		if (ticketPrice !== "" && (!Number.isFinite(Number(ticketPrice)) || Number(ticketPrice) < 0)) {
			this.props.Interface.addNoty("Error", "Ticket price should be a non-negative number");
			return;
		}

		if (minTicketsToBuy !== "" && (!Number.isFinite(Number(minTicketsToBuy)) || Number(minTicketsToBuy) < 0)) {
			this.props.Interface.addNoty("Error", "Minimum tickets to buy should be a non-negative number");
			return;
		}

		this.setState({
			isSettingsLoading: true,
			settingsError: "",
		});

		try {
			const config = await this.props.Interface.updateTicketsConfig({
				amount: Number(ticketPrice),
				minCount: Number(minTicketsToBuy),
				project: 0,
			});

			this.setState({
				showSettingsModal: false,
				ticketPrice: typeof config?.amount === "number" ? String(config.amount) : ticketPrice,
				minTicketsToBuy: typeof config?.minCount === "number" ? String(config.minCount) : minTicketsToBuy,
				rtpCompensation: this.normalizeRtpCompensationForState(config?.rtpCompensation),
				isSettingsLoading: false,
				settingsError: "",
			});

			this.props.Interface.addNoty("Success", "Ticket settings updated");
		} catch (error) {
			this.setState({
				isSettingsLoading: false,
				settingsError: error?.text || error?.message || "Failed to update ticket settings",
			});

			this.props.Interface.addNoty("Error", "Failed to update ticket settings");
		}
	};

	handleRtpGiftSettingsSave = async () => {
		let rtpCompensation = null;
		try {
			rtpCompensation = this.getRtpCompensationPayload();
		} catch (error) {
			this.setState({ settingsError: error.message });
			this.props.Interface.addNoty("Error", error.message);
			return;
		}

		this.setState({
			isSettingsLoading: true,
			settingsError: "",
		});

		try {
			const config = await this.props.Interface.updateTicketsConfig({
				rtpCompensation,
				project: 0,
			});

			this.setState({
				showSettingsModal: false,
				ticketPrice: typeof config?.amount === "number" ? String(config.amount) : this.state.ticketPrice,
				minTicketsToBuy: typeof config?.minCount === "number" ? String(config.minCount) : this.state.minTicketsToBuy,
				rtpCompensation: this.normalizeRtpCompensationForState(config?.rtpCompensation),
				isSettingsLoading: false,
				settingsError: "",
			});

			this.props.Interface.addNoty("Success", "RTP gift ticket settings updated");
		} catch (error) {
			this.setState({
				isSettingsLoading: false,
				settingsError: error?.text || error?.message || "Failed to update ticket settings",
			});

			this.props.Interface.addNoty("Error", "Failed to update ticket settings");
		}
	};

	openWriteOffAllModal = (items = this.state.selectedItems) => {
		if (!items.length) {
			this.props.Interface.addNoty("Error", "No players selected");
			return;
		}

		this.setState({
			writeOffItems: items,
			showWriteOffAllModal: true,
			writeOffError: "",
		});
	};

	openWriteOffAmountModal = (items = this.state.selectedItems) => {
		if (!items.length) {
			this.props.Interface.addNoty("Error", "No players selected");
			return;
		}

		this.setState({
			writeOffItems: items,
			showWriteOffAmountModal: true,
			writeOffAmount: "",
			writeOffError: "",
		});
	};

	closeWriteOffModals = () => {
		if (this.state.isWriteOffLoading) return;

		this.setState({
			showWriteOffAllModal: false,
			showWriteOffAmountModal: false,
			writeOffItems: [],
			writeOffAmount: "",
			writeOffError: "",
		});
	};

	handleWriteOff = async (mode) => {
		const uids = this.getWriteOffUids();

		if (!uids.length) {
			this.setState({ writeOffError: "No valid players selected" });
			return;
		}

		const payload = {
			uids,
			mode,
		};

		if (mode === "amount") {
			const amount = parseInt(this.state.writeOffAmount, 10);
			if (!Number.isInteger(amount) || amount <= 0) {
				this.setState({ writeOffError: "Enter a positive number of tickets" });
				return;
			}

			payload.amount = amount;
		}

		this.setState({
			isWriteOffLoading: true,
			writeOffError: "",
		});

		try {
			const result = await this.props.Interface.writeOffTickets(payload);
			const affected = Number(result?.affected) || 0;
			const totalDeducted = Number(result?.totalDeducted) || 0;

			this.props.Interface.addNoty("Success", `Tickets written off. Players updated: ${affected}. Total deducted: ${totalDeducted}`);

			this.setState(
				{
					selectedItems: [],
					writeOffItems: [],
					showWriteOffAllModal: false,
					showWriteOffAmountModal: false,
					writeOffAmount: "",
					writeOffError: "",
					isWriteOffLoading: false,
				},
				() => this.loadTickets(),
			);
		} catch (error) {
			this.setState({
				isWriteOffLoading: false,
				writeOffError: error?.text || error?.message || "Failed to write off tickets",
			});
		}
	};

	getValue(item, fields, fallback = "") {
		for (let i = 0; i < fields.length; i++) {
			const value = item[fields[i]];
			if (value !== null && typeof value !== "undefined" && value !== "") return value;
		}

		return fallback;
	}

	getUniqueSelectedItems(items = []) {
		const uniqueItems = new Map();

		items.forEach((item) => {
			if (!item) return;

			const rowId = typeof item.rowId !== "undefined" ? String(item.rowId) : String(item.uid || "");
			if (!rowId.length) return;

			uniqueItems.set(rowId, item);
		});

		return [...uniqueItems.values()];
	}

	getTableItem(item, index) {
		const uid = this.getValue(item, ["uid", "playerUid", "userId", "user_id", "player_id"], "-");
		const currentTickets = this.getValue(item, ["currentTickets", "balance", "ticketsBalance", "ticketBalance", "currentBalance"], 0);
		const periodTickets = this.getValue(item, ["periodTickets", "tickets", "ticketsCount", "receivedTickets", "count", "amount"], 0);
		const lastTicketDate = this.getValue(item, ["lastTicketDate", "lastDate", "date", "createdAt", "updatedAt"], "");
		const type = this.getValue(item, ["type", "ticketType", "source"], this.state.ticketType.value);

		return {
			...item,
			rowId: Number.isInteger(uid) && uid > 0 ? String(uid) : `${uid}-${this.getValue(item, ["id"], index)}`,
			uid,
			currentTickets,
			periodTickets,
			lastTicketDate,
			type,
		};
	}

	getTableData(response, page, pageSize) {
		const items = Array.isArray(response) ? response : response?.items || response?.data || response?.list || [];
		const pagination = response?.pagination || {};
		const count = pagination.total || response?.total || response?.count || items.length;
		const total = pagination.totalPages || pagination.pages || response?.totalPages || response?.pages || Math.max(1, Math.ceil(count / pageSize));
		const current = pagination.page || response?.page || page;

		return {
			items: items.map((item, index) => this.getTableItem(item, index)),
			pagination: {
				current,
				total,
				count,
			},
		};
	}

	loadTickets = ({ page = this.state.pagination.current, sorting = this.state.sorting, pageSize = this.state.pageSize, preserveSelection = false } = {}) => {
		const dateRangeValidation = this.isDateRangeValid(this.state.dateRange);
		const ticketsFiltersValidation = this.getTicketsFiltersValidation();

		if (!dateRangeValidation.valid) {
			this.setState({
				loaded: true,
				error: {
					text: dateRangeValidation.errorMessage,
					code: 0,
					timestamp: Date.now(),
				},
			});
			return;
		}

		if (!ticketsFiltersValidation.valid) {
			this.setState({
				loaded: true,
				error: {
					text: ticketsFiltersValidation.errorMessage,
					code: 0,
					timestamp: Date.now(),
				},
			});
			return;
		}

		const sortingColumn = sorting.sortingColumn || this.state.sorting.sortingColumn;

		this.setState((prevState) => ({
			loaded: false,
			selectedItems: preserveSelection ? prevState.selectedItems : [],
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
			sorting: {
				...prevState.sorting,
				...sorting,
				sortingColumn,
			},
			pagination: {
				...prevState.pagination,
				current: page,
			},
			pageSize,
		}));

		this.props.Interface.getPlayerTickets(this.getRequestData(page, pageSize, sortingColumn))
			.then((response) => {
				const tableData = this.getTableData(response, page, pageSize);

				this.setState((prevState) => ({
					loaded: true,
					data: tableData.items,
					pagination: tableData.pagination,
					selectedItems: preserveSelection ? this.getUniqueSelectedItems(prevState.selectedItems) : [],
				}));
			})
			.catch((error) => {
				this.setState((prevState) => ({
					loaded: true,
					data: [],
					selectedItems: preserveSelection ? this.getUniqueSelectedItems(prevState.selectedItems) : [],
					error: {
						text: error?.text || error?.message || "Failed to load tickets",
						code: error?.code || 0,
						timestamp: error?.timestamp || Date.now(),
					},
					pagination: {
						current: page,
						total: 1,
						count: 0,
					},
				}));
			});
	};

	loadGiftStats = ({ page = this.state.gift.pagination.current, sorting = this.state.gift.sorting, pageSize = this.state.gift.pageSize } = {}) => {
		const dateRangeValidation = this.isDateRangeValid(this.state.gift.dateRange);
		const giftFiltersValidation = this.getGiftFiltersValidation();

		if (!dateRangeValidation.valid) {
			this.setState((prevState) => ({
				gift: {
					...prevState.gift,
					loaded: true,
					error: {
						text: dateRangeValidation.errorMessage,
						code: 0,
						timestamp: Date.now(),
					},
				},
			}));
			return;
		}

		if (!giftFiltersValidation.valid) {
			this.setState((prevState) => ({
				gift: {
					...prevState.gift,
					loaded: true,
					error: {
						text: giftFiltersValidation.errorMessage,
						code: 0,
						timestamp: Date.now(),
					},
				},
			}));
			return;
		}

		const sortingColumn = sorting.sortingColumn || this.state.gift.sorting.sortingColumn;

		this.setState((prevState) => ({
			gift: {
				...prevState.gift,
				loaded: false,
				error: {
					text: "",
					code: 0,
					timestamp: 0,
				},
				sorting: {
					...prevState.gift.sorting,
					...sorting,
					sortingColumn,
				},
				pagination: {
					...prevState.gift.pagination,
					current: page,
				},
				pageSize,
			},
		}));

		this.props.Interface.getGiftTicketsStats(this.getGiftRequestData(page, pageSize, sortingColumn))
			.then((response) => {
				const pagination = response?.pagination || {};
				const events = response?.events || [];
				const total = pagination.total || response?.total || events.length;

				this.setState((prevState) => ({
					gift: {
						...prevState.gift,
						loaded: true,
						events,
						daily: response?.daily || [],
						summary: {
							...prevState.gift.summary,
							...(response?.summary || {}),
						},
						pagination: {
							current: pagination.page || page,
							total: pagination.totalPages || Math.max(1, Math.ceil(total / pageSize)),
							count: total,
						},
					},
				}));
			})
			.catch((error) => {
				this.setState((prevState) => ({
					gift: {
						...prevState.gift,
						loaded: true,
						events: [],
						daily: [],
						summary: {
							totalTickets: 0,
							playersCount: 0,
						},
						error: {
							text: error?.text || error?.message || "Failed to load gift tickets",
							code: error?.code || 0,
							timestamp: error?.timestamp || Date.now(),
						},
						pagination: {
							current: page,
							total: 1,
							count: 0,
						},
					},
				}));
			});
	};

	handleTicketsTabChange = ({ detail }) => {
		this.saveStoredTicketsTab(detail.activeTabId);

		this.setState({ activeTicketsTab: detail.activeTabId }, () => {
			if (detail.activeTabId === "gift" && !this.state.gift.loaded) {
				this.loadGiftStats({ page: 1 });
			}
		});
	};

	applyFilters = () => {
		this.loadTickets({ page: 1 });
	};

	applyGiftFilters = () => {
		this.loadGiftStats({ page: 1 });
	};

	resetFilters = () => {
		const pageSize = this.state.pageSize;
		const sorting = {
			sortingColumn: {
				column: "lastTicketDate",
				order: "DESC",
			},
			isDescending: true,
		};

		this.setState(
			{
				dateRange: this.getEmptyDateRange(),
				ticketType: this.getTypeOptions()[0],
				uid: "",
				periodTicketsFrom: "",
				periodTicketsTo: "",
				currentTicketsFrom: "",
				currentTicketsTo: "",
				selectedItems: [],
				sorting,
				pagination: {
					current: 1,
					total: 1,
					count: 0,
				},
				error: {
					text: "",
					code: 0,
					timestamp: 0,
				},
			},
			() => this.loadTickets({ page: 1, sorting, pageSize }),
		);
	};

	resetGiftFilters = () => {
		const pageSize = this.state.gift.pageSize;
		const sorting = {
			sortingColumn: {
				column: "issuedAt",
				order: "DESC",
			},
			isDescending: true,
		};

		this.setState(
			(prevState) => ({
				gift: {
					...prevState.gift,
					dateRange: this.getEmptyDateRange(),
					uid: "",
					ticketsFrom: "",
					ticketsTo: "",
					rtpFrom: "",
					rtpTo: "",
					sorting,
					pagination: {
						current: 1,
						total: 1,
						count: 0,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
				},
			}),
			() => this.loadGiftStats({ page: 1, sorting, pageSize }),
		);
	};

	handleSortingChange = ({ detail }) => {
		const sorting = {
			sortingColumn: {
				column: detail.sortingColumn.sortingField,
				order: detail.isDescending ? "DESC" : "ASC",
			},
			isDescending: detail.isDescending,
		};

		this.loadTickets({ page: 1, sorting });
	};

	handleGiftSortingChange = ({ detail }) => {
		const sorting = {
			sortingColumn: {
				column: detail.sortingColumn.sortingField,
				order: detail.isDescending ? "DESC" : "ASC",
			},
			isDescending: detail.isDescending,
		};

		this.loadGiftStats({ page: 1, sorting });
	};

	handlePageChange = ({ detail }) => {
		this.loadTickets({ page: detail.currentPageIndex, preserveSelection: true });
	};

	handleGiftPageChange = ({ detail }) => {
		this.loadGiftStats({ page: detail.currentPageIndex });
	};

	handleSelectionChange = ({ detail }) => {
		this.setState((prevState) => {
			const currentPageRowIds = new Set(prevState.data.map((item) => item.rowId));
			const preservedItems = prevState.selectedItems.filter((item) => !currentPageRowIds.has(item.rowId));

			return {
				selectedItems: this.getUniqueSelectedItems([...preservedItems, ...detail.selectedItems]),
			};
		});
	};

	handleFilterKeyPress = (event) => {
		if (event.detail.key === "Enter") {
			setTimeout(() => {
				this.applyFilters();
			}, 0);
		}
	};

	handleGiftFilterKeyPress = (event) => {
		if (event.detail.key === "Enter") {
			setTimeout(() => {
				this.applyGiftFilters();
			}, 0);
		}
	};

	handlePreferencesConfirm = ({ detail }) => {
		const pageSize = Number(detail.pageSize);
		localStorage.setItem("tickets.perPage", pageSize);
		this.loadTickets({ page: 1, pageSize, preserveSelection: true });
	};

	handleGiftPreferencesConfirm = ({ detail }) => {
		const pageSize = Number(detail.pageSize);
		localStorage.setItem("giftTickets.perPage", pageSize);
		this.loadGiftStats({ page: 1, pageSize });
	};

	formatNumber(value) {
		if (!Number.isFinite(Number(value))) return value || "0";

		return new Intl.NumberFormat("ru-RU").format(Number(value));
	}

	formatDate(value) {
		if (!value) return "-";

		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return value;

		return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
	}

	formatPercent(value) {
		if (value === null || typeof value === "undefined" || value === "") return "-";
		if (!Number.isFinite(Number(value))) return "-";

		return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(Number(value))}%`;
	}

	formatMoney(value) {
		if (value === null || typeof value === "undefined" || value === "") return "-";
		if (!Number.isFinite(Number(value))) return "-";

		return `${new Intl.NumberFormat("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) / 100)} RUB.`;
	}

	getTypeLabel(value) {
		switch (value) {
			case "buy":
				return "Purchased";
			case "win":
				return "Won";
			case "all":
				return "All";
			default:
				return value || "-";
		}
	}

	renderRtpRule(rule, index) {
		const isSettingsLoading = this.state.isSettingsLoading;

		return (
			<Container key={`rtp-rule-${index}`} header={<Header variant="h3">Rule {index + 1}</Header>}>
				<SpaceBetween direction="vertical" size="s">
					<Grid gridDefinition={[{ colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }]}>
						<FormField label="Min operator">
							<Select selectedOption={this.getSelectedOperator(rule.minOperator, "min")} options={this.getRtpOperatorOptions("min")} placeholder="None" disabled={isSettingsLoading || rule.min === ""} onChange={({ detail }) => this.updateRtpRule(index, { minOperator: detail.selectedOption?.value || null })} />
						</FormField>
						<FormField label="Min RTP">
							<Input type="number" value={rule.min} placeholder="60" disabled={isSettingsLoading} onChange={({ detail }) => this.updateRtpRule(index, { min: detail.value, minOperator: detail.value === "" ? null : rule.minOperator || ">=" })} />
						</FormField>
						<FormField label="Max operator">
							<Select selectedOption={this.getSelectedOperator(rule.maxOperator, "max")} options={this.getRtpOperatorOptions("max")} placeholder="None" disabled={isSettingsLoading || rule.max === ""} onChange={({ detail }) => this.updateRtpRule(index, { maxOperator: detail.selectedOption?.value || null })} />
						</FormField>
						<FormField label="Max RTP">
							<Input type="number" value={rule.max} placeholder="85" disabled={isSettingsLoading} onChange={({ detail }) => this.updateRtpRule(index, { max: detail.value, maxOperator: detail.value === "" ? null : rule.maxOperator || "<" })} />
						</FormField>
						<FormField label="Tickets">
							<Input type="number" value={rule.tickets} placeholder="1" disabled={isSettingsLoading} onChange={({ detail }) => this.updateRtpRule(index, { tickets: detail.value })} />
						</FormField>
						<FormField label="Action">
							<Button variant="normal" disabled={isSettingsLoading || this.state.rtpCompensation.rules.length <= 1} onClick={() => this.removeRtpRule(index)}>
								Remove
							</Button>
						</FormField>
					</Grid>
				</SpaceBetween>
			</Container>
		);
	}

	renderSettingsModal() {
		const { showSettingsModal, settingsModalType, ticketPrice, minTicketsToBuy, isSettingsLoading, rtpCompensation, settingsError } = this.state;
		const isRtpGiftSettings = settingsModalType === "rtpGift";

		return (
			<Modal
				visible={showSettingsModal}
				onDismiss={this.closeSettingsModal}
				header={isRtpGiftSettings ? "RTP gift ticket settings" : "Ticket settings"}
				size="large"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="link" onClick={this.closeSettingsModal} disabled={isSettingsLoading}>
								Close
							</Button>
							<Button variant="primary" onClick={this.handleSettingsSave} loading={isSettingsLoading} disabled={isSettingsLoading}>
								Apply
							</Button>
						</SpaceBetween>
					</Box>
				}>
				<SpaceBetween direction="vertical" size="m">
					{settingsError ? <Alert type="error">{settingsError}</Alert> : null}
					{isRtpGiftSettings ? (
						<>
							<Toggle checked={rtpCompensation.enabled} disabled={isSettingsLoading} onChange={({ detail }) => this.updateRtpCompensation({ enabled: detail.checked })}>
								Enabled
							</Toggle>
							<FormField label="Rounds for RTP calculation">
								<Input type="number" value={rtpCompensation.windowRounds} placeholder="10" disabled={isSettingsLoading} onChange={({ detail }) => this.updateRtpCompensation({ windowRounds: detail.value })} />
							</FormField>
							<SpaceBetween direction="vertical" size="s">
								{rtpCompensation.rules.map((rule, index) => this.renderRtpRule(rule, index))}
							</SpaceBetween>
							<Button variant="normal" onClick={this.addRtpRule} disabled={isSettingsLoading}>
								Add RTP rule
							</Button>
						</>
					) : (
						<>
							<FormField label="Ticket price">
								<Input type="number" value={ticketPrice} placeholder="0" disabled={isSettingsLoading} onChange={({ detail }) => this.setState({ ticketPrice: detail.value, settingsError: "" })} />
							</FormField>
							<FormField label="Minimum tickets to buy">
								<Input type="number" value={minTicketsToBuy} placeholder="0" disabled={isSettingsLoading} onChange={({ detail }) => this.setState({ minTicketsToBuy: detail.value, settingsError: "" })} />
							</FormField>
						</>
					)}
				</SpaceBetween>
			</Modal>
		);
	}

	renderGiftSummary() {
		const summary = this.state.gift.summary;

		return (
			<ColumnLayout columns={2} variant="text-grid">
				<div>
					<Box variant="awsui-key-label">Total issued tickets</Box>
					<Box variant="h2">{this.formatNumber(summary.totalTickets)}</Box>
				</div>
				<div>
					<Box variant="awsui-key-label">Players</Box>
					<Box variant="h2">{this.formatNumber(summary.playersCount)}</Box>
				</div>
			</ColumnLayout>
		);
	}

	renderGiftTicketsReport() {
		const { gift } = this.state;

		return (
			<SpaceBetween direction="vertical" size="m">
				{gift.error.text.length ? (
					<Alert
						type="error"
						header="Error loading gift tickets"
						dismissible
						onDismiss={() =>
							this.setState((prevState) => ({
								gift: {
									...prevState.gift,
									error: {
										text: "",
										code: 0,
										timestamp: 0,
									},
								},
							}))
						}>
						<Box variant="p">{gift.error.text}</Box>
						<hr />
						<Box variant="p">
							Request error code: {gift.error.code} | Request timestamp: {gift.error.timestamp}
						</Box>
					</Alert>
				) : null}

				<Container header={<Header>Gift tickets summary</Header>}>{this.renderGiftSummary()}</Container>

				<Table
					stickyHeader
					trackBy="id"
					loading={!gift.loaded}
					items={gift.events}
					sortingColumn={{ sortingField: gift.sorting.sortingColumn.column }}
					sortingDescending={gift.sorting.isDescending}
					onSortingChange={this.handleGiftSortingChange}
					columnDefinitions={[
						{
							id: "uid",
							header: "UID",
							sortingField: "uid",
							cell: (item) => (
								<Button
									variant="link"
									onClick={() => {
										this.props.Interface.loadInfo(item.uid);
									}}>
									{item.uid}
								</Button>
							),
						},
						{
							id: "issuedAt",
							header: "Issued at",
							sortingField: "issuedAt",
							cell: (item) => this.formatDate(item.issuedAt),
						},
						{
							id: "ticketsIssued",
							header: "Tickets issued",
							sortingField: "ticketsIssued",
							cell: (item) => this.formatNumber(item.ticketsIssued),
						},
						{
							id: "triggerRoundId",
							header: "Trigger round ID",
							sortingField: "triggerRoundId",
							cell: (item) => item.triggerRoundId || "-",
						},
						{
							id: "sessionId",
							header: "Session ID",
							cell: (item) => item.sessionId || "-",
						},
						{
							id: "rtp",
							header: "RTP",
							sortingField: "rtp",
							cell: (item) => this.formatPercent(item.rtp),
						},
						{
							id: "rounds",
							header: "Rounds",
							sortingField: "rounds",
							cell: (item) => (item.rounds === null ? "-" : this.formatNumber(item.rounds)),
						},
						{
							id: "spent",
							header: "Spent, RUB",
							sortingField: "spent",
							cell: (item) => this.formatMoney(item.spent),
						},
						{
							id: "won",
							header: "Won, RUB",
							sortingField: "won",
							cell: (item) => this.formatMoney(item.won),
						},
					]}
					pagination={<Pagination disabled={!gift.loaded} currentPageIndex={gift.pagination.current} pagesCount={gift.pagination.total} onChange={this.handleGiftPageChange} />}
					preferences={
						<CollectionPreferences
							title="Preferences"
							confirmLabel="Confirm"
							cancelLabel="Cancel"
							preferences={{ pageSize: gift.pageSize }}
							pageSizePreference={{
								title: "Rows per page",
								options: [
									{ value: 20, label: "20" },
									{ value: 50, label: "50" },
									{ value: 100, label: "100" },
								],
							}}
							onConfirm={this.handleGiftPreferencesConfirm}
						/>
					}
					filter={
						<SpaceBetween direction="vertical" size="m">
							<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
								<FormField label="Gift issue date">
									<DateRangePicker
										value={gift.dateRange}
										placeholder="All time"
										relativeOptions={[]}
										isValidRange={this.isDateRangeValid}
										onKeyDown={this.handleGiftFilterKeyPress}
										onChange={({ detail }) => this.setState((prevState) => ({ gift: { ...prevState.gift, dateRange: this.getDateRangeValue(detail.value) } }))}
										i18nStrings={{
											todayAriaLabel: "Today",
											nextMonthAriaLabel: "Next month",
											previousMonthAriaLabel: "Previous month",
											customRelativeRangeDurationLabel: "Duration",
											customRelativeRangeDurationPlaceholder: "Enter duration",
											customRelativeRangeOptionLabel: "Custom range",
											customRelativeRangeOptionDescription: "Define a custom range in the past",
											customRelativeRangeUnitLabel: "Unit of time",
											formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
											dateTimeConstraintText: "Date and time must be within the selected range.",
											renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
											labelRelativeRange: "Relative range",
											labelAbsoluteRange: "Absolute range",
											dateTimePickerAriaLabel: "Date and time picker",
											dateTimePickerLabel: "Date and Time",
											relativeModeTitle: "Relative mode",
											absoluteModeTitle: "Absolute mode",
											cancelButtonLabel: "Cancel",
											applyButtonLabel: "Apply",
											clearButtonLabel: "Clear date range",
											showClearButton: true,
										}}
									/>
								</FormField>
								<FormField label="UID or UID list">
									<Input value={gift.uid} placeholder="123 or 123,456,789" onChange={({ detail }) => this.setState((prevState) => ({ gift: { ...prevState.gift, uid: detail.value } }))} onKeyDown={this.handleGiftFilterKeyPress} />
								</FormField>
								<FormField label="Tickets issued from">
									<Input type="number" value={gift.ticketsFrom} placeholder="1" onChange={({ detail }) => this.setState((prevState) => ({ gift: { ...prevState.gift, ticketsFrom: detail.value } }))} onKeyDown={this.handleGiftFilterKeyPress} />
								</FormField>
							</Grid>
							<Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}>
								<FormField label="Tickets issued to">
									<Input type="number" value={gift.ticketsTo} placeholder="2" onChange={({ detail }) => this.setState((prevState) => ({ gift: { ...prevState.gift, ticketsTo: detail.value } }))} onKeyDown={this.handleGiftFilterKeyPress} />
								</FormField>
								<FormField label="RTP from">
									<Input type="number" value={gift.rtpFrom} placeholder="0" onChange={({ detail }) => this.setState((prevState) => ({ gift: { ...prevState.gift, rtpFrom: detail.value } }))} onKeyDown={this.handleGiftFilterKeyPress} />
								</FormField>
								<FormField label="RTP to">
									<Input type="number" value={gift.rtpTo} placeholder="85" onChange={({ detail }) => this.setState((prevState) => ({ gift: { ...prevState.gift, rtpTo: detail.value } }))} onKeyDown={this.handleGiftFilterKeyPress} />
								</FormField>
								<FormField>
									<SpaceBetween direction="horizontal" size="xs">
										<Button variant="primary" onClick={this.applyGiftFilters} loading={!gift.loaded}>
											Apply
										</Button>
										<Button variant="normal" onClick={this.resetGiftFilters} disabled={!gift.loaded}>
											Reset
										</Button>
										<Button variant="icon" iconName="refresh" onClick={() => this.loadGiftStats()} disabled={!gift.loaded} />
									</SpaceBetween>
								</FormField>
							</Grid>
						</SpaceBetween>
					}
					header={
						<Header
							actions={
								<Button variant="primary" onClick={() => this.openSettingsModal("rtpGift")}>
									RTP gift settings
								</Button>
							}>
							Gift ticket events ({gift.pagination.count})
						</Header>
					}
					empty="No gift tickets found"
				/>

				<Table
					trackBy="rowId"
					loading={!gift.loaded}
					items={gift.daily.map((item) => ({ ...item, rowId: `${item.uid}-${item.date}` }))}
					columnDefinitions={[
						{
							id: "uid",
							header: "UID",
							cell: (item) => item.uid,
						},
						{
							id: "date",
							header: "Date",
							cell: (item) => item.date || "-",
						},
						{
							id: "ticketsIssued",
							header: "Tickets issued",
							cell: (item) => this.formatNumber(item.ticketsIssued),
						},
						{
							id: "grants",
							header: "Grants",
							cell: (item) => this.formatNumber(item.grants),
						},
					]}
					header={<Header>Daily gift tickets by UID</Header>}
					empty="No daily gift ticket data"
				/>
			</SpaceBetween>
		);
	}

	render() {
		const { loaded, error, data, selectedItems, writeOffItems, showWriteOffAllModal, showWriteOffAmountModal, writeOffAmount, writeOffError, isWriteOffLoading, pagination, sorting, pageSize, dateRange, ticketType, uid, periodTicketsFrom, periodTicketsTo, currentTicketsFrom, currentTicketsTo, activeTicketsTab } = this.state;

		return (
			<>
				{this.renderSettingsModal()}

				<Modal
					visible={showWriteOffAllModal}
					onDismiss={this.closeWriteOffModals}
					header="Confirm ticket write-off"
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="xs">
								<Button variant="link" onClick={this.closeWriteOffModals} disabled={isWriteOffLoading}>
									Close
								</Button>
								<Button variant="primary" onClick={() => this.handleWriteOff("all")} loading={isWriteOffLoading} disabled={isWriteOffLoading}>
									Apply
								</Button>
							</SpaceBetween>
						</Box>
					}>
					<SpaceBetween direction="vertical" size="s">
						<Box>Are you sure you want to write off all tickets for {writeOffItems.length} selected player(s)?</Box>
						{writeOffError ? <Alert type="error">{writeOffError}</Alert> : null}
					</SpaceBetween>
				</Modal>

				<Modal
					visible={showWriteOffAmountModal}
					onDismiss={this.closeWriteOffModals}
					header="Write off ticket amount"
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="xs">
								<Button variant="link" onClick={this.closeWriteOffModals} disabled={isWriteOffLoading}>
									Close
								</Button>
								<Button variant="primary" onClick={() => this.handleWriteOff("amount")} loading={isWriteOffLoading} disabled={isWriteOffLoading || writeOffAmount <= 0}>
									Apply
								</Button>
							</SpaceBetween>
						</Box>
					}>
					<SpaceBetween direction="vertical" size="s">
						<Box>Selected players: {writeOffItems.length}</Box>
						<FormField label="Tickets to write off" errorText={writeOffError}>
							<Input type="number" value={writeOffAmount} placeholder="1" onChange={({ detail }) => detail.value >= 0 && this.setState({ writeOffAmount: detail.value, writeOffError: "" })} />
						</FormField>
					</SpaceBetween>
				</Modal>

				<Tabs
					activeTabId={activeTicketsTab}
					onChange={this.handleTicketsTabChange}
					tabs={[
						{
							label: "Ticket balances",
							id: "tickets",
							content: (
								<>
									{error.text.length ? (
										<Box margin={{ bottom: "m" }}>
											<Alert
												type="error"
												header="Error loading data"
												dismissible
												onDismiss={() =>
													this.setState({
														error: {
															text: "",
															code: 0,
															timestamp: 0,
														},
													})
												}>
												<Box variant="p">{error.text}</Box>
												<hr />
												<Box variant="p">
													Request error code: {error.code} | Request timestamp: {error.timestamp}
												</Box>
											</Alert>
										</Box>
									) : null}

									<Table
										stickyHeader
										trackBy="rowId"
										loading={!loaded}
										items={data}
										selectedItems={selectedItems}
										selectionType="multi"
										onSelectionChange={this.handleSelectionChange}
										sortingColumn={{ sortingField: sorting.sortingColumn.column }}
										sortingDescending={sorting.isDescending}
										onSortingChange={this.handleSortingChange}
										columnDefinitions={[
											{
												id: "uid",
												header: "UID",
												sortingField: "uid",
												cell: (item) => (
													<Button
														variant="link"
														onClick={() => {
															this.props.Interface.loadInfo(item.uid);
														}}>
														{item.uid}
													</Button>
												),
											},
											{
												id: "currentTickets",
												header: "Current tickets",
												sortingField: "currentTickets",
												cell: (item) => this.formatNumber(item.currentTickets),
											},
											{
												id: "periodTickets",
												header: "Tickets in period",
												sortingField: "periodTickets",
												cell: (item) => this.formatNumber(item.periodTickets),
											},
											{
												id: "type",
												header: "Type",
												cell: (item) => this.getTypeLabel(item.type),
											},
											{
												id: "lastTicketDate",
												header: "Last ticket date",
												sortingField: "lastTicketDate",
												cell: (item) => this.formatDate(item.lastTicketDate),
											},
											{
												id: "actions",
												header: "Actions",
												cell: (item) => (
													<SpaceBetween direction="horizontal" size="xs">
														<Button variant="normal" onClick={() => this.openWriteOffAmountModal([item])} disabled={!loaded}>
															Write off amount
														</Button>
														<Button variant="normal" onClick={() => this.openWriteOffAllModal([item])} disabled={!loaded}>
															Write off all
														</Button>
													</SpaceBetween>
												),
											},
										]}
										pagination={<Pagination disabled={!loaded} currentPageIndex={pagination.current} pagesCount={pagination.total} onChange={this.handlePageChange} />}
										preferences={
											<CollectionPreferences
												title="Preferences"
												confirmLabel="Confirm"
												cancelLabel="Cancel"
												preferences={{ pageSize }}
												pageSizePreference={{
													title: "Rows per page",
													options: [
														{ value: 20, label: "20" },
														{ value: 50, label: "50" },
														{ value: 100, label: "100" },
														{ value: 500, label: "500" },
													],
												}}
												onConfirm={this.handlePreferencesConfirm}
											/>
										}
										filter={
											<SpaceBetween direction="vertical" size="m">
												<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
													<FormField label="Ticket receive date">
														<SpaceBetween direction="vertical" size="xs">
															<DateRangePicker
																value={dateRange}
																placeholder="All time"
																relativeOptions={[]}
																isValidRange={this.isDateRangeValid}
																onKeyDown={this.handleFilterKeyPress}
																onChange={({ detail }) => this.setState({ dateRange: this.getDateRangeValue(detail.value) })}
																i18nStrings={{
																	todayAriaLabel: "Today",
																	nextMonthAriaLabel: "Next month",
																	previousMonthAriaLabel: "Previous month",
																	customRelativeRangeDurationLabel: "Duration",
																	customRelativeRangeDurationPlaceholder: "Enter duration",
																	customRelativeRangeOptionLabel: "Custom range",
																	customRelativeRangeOptionDescription: "Define a custom range in the past",
																	customRelativeRangeUnitLabel: "Unit of time",
																	formatRelativeRange: ({ amount, unit, startDate, endDate }) => {
																		const start = new Date(startDate);
																		const end = new Date(endDate);

																		const locale = "en-GB";
																		const dateOptions = { year: "numeric", month: "long", day: "numeric" };
																		const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };

																		const formattedStart = start.toLocaleDateString(locale, dateOptions) + " " + start.toLocaleTimeString(locale, timeOptions);
																		const formattedEnd = end.toLocaleDateString(locale, dateOptions) + " " + end.toLocaleTimeString(locale, timeOptions);

																		return `${formattedStart} - ${formattedEnd}`;
																	},
																	formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
																	dateTimeConstraintText: "Date and time must be within the selected range.",
																	renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
																	labelRelativeRange: "Relative range",
																	labelAbsoluteRange: "Absolute range",
																	dateTimePickerAriaLabel: "Date and time picker",
																	dateTimePickerLabel: "Date and Time",
																	relativeModeTitle: "Relative mode",
																	absoluteModeTitle: "Absolute mode",
																	cancelButtonLabel: "Cancel",
																	applyButtonLabel: "Apply",
																	clearButtonLabel: "Clear date range",
																	showClearButton: true,
																}}
															/>
														</SpaceBetween>
													</FormField>
													<FormField label="Ticket type">
														<Select selectedOption={ticketType} options={this.getTypeOptions()} onChange={({ detail }) => this.setState({ ticketType: detail.selectedOption })} onKeyDown={this.handleFilterKeyPress} />
													</FormField>
													<FormField label="UID or UID list">
														<Input value={uid} placeholder="123 or 123,456,789" onChange={({ detail }) => this.setState({ uid: detail.value })} onKeyDown={this.handleFilterKeyPress} />
													</FormField>
												</Grid>
												<Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}>
													<FormField label="Tickets in period from">
														<Input type="number" value={periodTicketsFrom} placeholder="1" onChange={({ detail }) => this.setState({ periodTicketsFrom: detail.value })} onKeyDown={this.handleFilterKeyPress} />
													</FormField>
													<FormField label="Tickets in period to">
														<Input type="number" value={periodTicketsTo} placeholder="100" onChange={({ detail }) => this.setState({ periodTicketsTo: detail.value })} onKeyDown={this.handleFilterKeyPress} />
													</FormField>
													<FormField label="Current tickets from">
														<Input type="number" value={currentTicketsFrom} placeholder="1" onChange={({ detail }) => this.setState({ currentTicketsFrom: detail.value })} onKeyDown={this.handleFilterKeyPress} />
													</FormField>
													<FormField label="Current tickets to">
														<Input type="number" value={currentTicketsTo} placeholder="100" onChange={({ detail }) => this.setState({ currentTicketsTo: detail.value })} onKeyDown={this.handleFilterKeyPress} />
													</FormField>
												</Grid>
												<Grid gridDefinition={[{ colspan: 12 }]}>
													<FormField>
														<SpaceBetween direction="horizontal" size="xs">
															<Button variant="primary" onClick={this.applyFilters} loading={!loaded}>
																Apply
															</Button>
															<Button variant="normal" onClick={this.resetFilters} disabled={!loaded}>
																Reset
															</Button>
															<Button variant="icon" iconName="refresh" onClick={() => this.loadTickets()} disabled={!loaded} />
														</SpaceBetween>
													</FormField>
												</Grid>
											</SpaceBetween>
										}
										header={
											<Header
												description="Players with tickets for the selected period"
												actions={
													<SpaceBetween direction="horizontal" size="xs">
														<Button variant="primary" onClick={() => this.openSettingsModal("tickets")}>
															Ticket settings
														</Button>
														<Button variant="normal" onClick={() => this.openWriteOffAmountModal(this.state.selectedItems)} disabled={!loaded || !selectedItems.length}>
															Write off amount
														</Button>
														<Button variant="normal" onClick={() => this.openWriteOffAllModal(this.state.selectedItems)} disabled={!loaded || !selectedItems.length}>
															Write off all
														</Button>
														<Button variant="link" disabled={true} color="text-status-inactive">
															Selected: {selectedItems.length}
														</Button>
														<Button variant="link" disabled={true} color="text-status-inactive">
															Total: {pagination.count}
														</Button>
													</SpaceBetween>
												}>
												Tickets ({pagination.count})
											</Header>
										}
										empty="No players found"
									/>
								</>
							),
						},
						{
							label: "Gift tickets",
							id: "gift",
							content: this.renderGiftTicketsReport(),
						},
					]}
				/>
			</>
		);
	}
}

export default PrizeTickets;
